import { z } from 'zod';

import { t } from '../i18n/validation.js';

type NumberFieldOptions = {
  integer?: boolean;
  integerMessage?: string;
  min?: number;
  minMessage?: string;
  max?: number;
  maxMessage?: string;
  gt?: number;
  gtMessage?: string;
  requiredMessage?: string;
  invalidMessage?: string;
};

const numberField = (label: string, options: NumberFieldOptions = {}) => {
  const requiredMessage = options.requiredMessage ?? t('validation.required', { field: label });
  const invalidMessage = options.invalidMessage ?? t('validation.invalidNumber', { field: label });
  const integerMessage = options.integerMessage ?? t('validation.mustBeInteger', { field: label });

  const baseSchema = z.union([
    z
      .string({ error: (issue) => (issue.input === undefined ? requiredMessage : invalidMessage) })
      .trim()
      .min(1, requiredMessage)
      .transform((value) => Number(value)),
    z.number({ error: invalidMessage }),
  ]);

  return baseSchema.superRefine((value, ctx) => {
    if (Number.isNaN(value) || !Number.isFinite(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: invalidMessage });
      return;
    }

    if (options.integer && !Number.isInteger(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: integerMessage });
    }

    if (typeof options.min === 'number' && value < options.min) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          options.minMessage ??
          t('validation.mustBeGreaterOrEqual', { field: label, min: options.min }),
      });
    }

    if (typeof options.max === 'number' && value > options.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          options.maxMessage ??
          t('validation.mustBeLessOrEqual', { field: label, max: options.max }),
      });
    }

    if (typeof options.gt === 'number' && value <= options.gt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          options.gtMessage ?? t('validation.mustBeGreaterThan', { field: label, min: options.gt }),
      });
    }
  });
};

export const buildScanRangesSchema = (
  minAllowedMhz: number | null,
  maxAllowedMhz: number | null,
) => {
  const finiteNumber = (message: string) =>
    z.coerce.number({ error: message }).refine(Number.isFinite, message);

  let channelFrequencySchema = finiteNumber(t('validation.frequency.frequencyInvalid'));
  let startFrequencyRangeSchema = finiteNumber(t('validation.frequency.startInvalid'));
  let endFrequencyRangeSchema = finiteNumber(t('validation.frequency.endInvalid'));

  if (minAllowedMhz !== null && minAllowedMhz > 0) {
    channelFrequencySchema = channelFrequencySchema.refine(
      (value: number) => value >= minAllowedMhz,
      {
        message: t('validation.frequency.frequencyMin', { min: minAllowedMhz }),
      },
    );
    startFrequencyRangeSchema = startFrequencyRangeSchema.refine(
      (value: number) => value >= minAllowedMhz,
      {
        message: t('validation.frequency.startMin', { min: minAllowedMhz }),
      },
    );
    endFrequencyRangeSchema = endFrequencyRangeSchema.refine(
      (value: number) => value >= minAllowedMhz,
      {
        message: t('validation.frequency.endMin', { min: minAllowedMhz }),
      },
    );
  }

  if (maxAllowedMhz !== null && maxAllowedMhz > 0) {
    channelFrequencySchema = channelFrequencySchema.refine(
      (value: number) => value <= maxAllowedMhz,
      {
        message: t('validation.frequency.frequencyMax', { max: maxAllowedMhz }),
      },
    );
    startFrequencyRangeSchema = startFrequencyRangeSchema.refine(
      (value: number) => value <= maxAllowedMhz,
      {
        message: t('validation.frequency.startMax', { max: maxAllowedMhz }),
      },
    );
    endFrequencyRangeSchema = endFrequencyRangeSchema.refine(
      (value: number) => value <= maxAllowedMhz,
      {
        message: t('validation.frequency.endMax', { max: maxAllowedMhz }),
      },
    );
  }

  const newChannelSchema = z.object({
    band: z
      .string({
        error: (issue) =>
          issue.input === undefined
            ? t('validation.scanRanges.bandRequired')
            : t('validation.scanRanges.bandInvalid'),
      })
      .trim()
      .min(1, t('validation.scanRanges.bandRequired'))
      .max(120, t('validation.scanRanges.bandMaxLength')),
    group: z
      .string({
        error: (issue) =>
          issue.input === undefined
            ? t('validation.scanRanges.groupRequired')
            : t('validation.scanRanges.groupInvalid'),
      })
      .trim()
      .min(1, t('validation.scanRanges.groupRequired'))
      .max(120, t('validation.scanRanges.groupMaxLength')),
    channel: numberField('Channel', {
      requiredMessage: t('validation.scanRanges.channelRequired'),
      invalidMessage: t('validation.scanRanges.channelInvalid'),
      integer: true,
      integerMessage: t('validation.scanRanges.channelInteger'),
      min: 0,
      minMessage: t('validation.scanRanges.channelMin'),
    }),
    freq: channelFrequencySchema,
    isDigital: z
      .boolean()
      .optional()
      .transform((value: boolean | undefined) => Boolean(value)),
    enabled: z
      .boolean()
      .optional()
      .transform((value: boolean | undefined) => value !== false),
  });
  const newRangeSchema = z
    .object({
      startFrequency: startFrequencyRangeSchema,
      endFrequency: endFrequencyRangeSchema,
      step: numberField('Step', {
        requiredMessage: t('validation.scanRanges.stepRequired'),
        invalidMessage: t('validation.scanRanges.stepInvalid'),
        gt: 0,
        gtMessage: t('validation.scanRanges.stepMin'),
      }),
      isDigital: z
        .boolean()
        .optional()
        .transform((value: boolean | undefined) => Boolean(value)),
      enabled: z
        .boolean()
        .optional()
        .transform((value: boolean | undefined) => value !== false),
    })
    .superRefine((value, ctx) => {
      if (value.startFrequency >= value.endFrequency) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['endFrequency'],
          message: t('validation.frequency.endGreaterThanStart'),
        });
      }
    });

  return z.object({
    channels: z.array(newChannelSchema),
    ranges: z.array(newRangeSchema),
  });
};

export const scanRangesSchema = buildScanRangesSchema(null, null);

export type DeviceScanRangesFormValues = z.infer<typeof scanRangesSchema>;
export type ChannelField = Extract<keyof DeviceScanRangesFormValues['channels'][number], string>;
export type RangeField = Extract<keyof DeviceScanRangesFormValues['ranges'][number], string>;

export const channelFields: readonly ChannelField[] = [
  'band',
  'group',
  'channel',
  'freq',
  'isDigital',
  'enabled',
];

export const rangeFields: readonly RangeField[] = [
  'startFrequency',
  'endFrequency',
  'step',
  'isDigital',
  'enabled',
];

export const isChannelField = (value: unknown): value is ChannelField =>
  typeof value === 'string' && channelFields.includes(value as ChannelField);

export const isRangeField = (value: unknown): value is RangeField =>
  typeof value === 'string' && rangeFields.includes(value as RangeField);
